// Supervisor wallboard (#/wallboard; supervisor, admin): the prototype's renderSup with live data from
// GET /api/wallboard, refreshed every 2 s while the screen is open (api-phase2.md "Wallboard").
//   tiles   queue by severity, oldest waiting, SLA breaches, accept within SLA, active / parked, held sync
//           changes, open work items
//   roster  presence, active / parked against the cap, current offer countdown, accepted / expired / declined
//           today, average handle time; pause / resume routing, Reassign, view their events
//   events  every open event: state, owner or target, park, age, severity, overflowed; Take, Force raise, Reassign
//   storms
//   Phase 3 (features.agent): AI tiles (events with the AI now, auto-resolved in the last 24 h, missed threats over
//   7 days from GET /api/agent/report) and the blind QA review queue (GET /api/qa), as in the prototype's renderSup.
//   Phase 3 follow-up / Phase 4: the `ai` block feeds tonight's funnel (alarms in → AI resolved → to operators), the
//   AI "now" tiles (triaging, auto-resolving, pending approvals, active watches, QA pending) and the global pause banner.
//   Supervisors can Take an event from the AI (agent_working / settling), which overrides the auto-resolve.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorText, isConflict, parkReasonLabel, SEVERITIES, type EventSummary, type QaItem, type Wallboard, type WallboardAi, type WallboardOperator } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { Empty, ErrorBox, InlineError, Loading } from '../components/ui.tsx';
import { Drawer } from '../console/Drawers.tsx';
import { waitingFor } from '../console/Queue.tsx';
import { VerdictChip } from '../console/Agent.tsx';
import { stateHint, stateLabel } from '../console/ai.ts';
import { Since } from '../console/Ticks.tsx';
import { camShort, dur, human, ms, SEV_CLS, SEV_LABEL, SEV_RANK } from '../console/util.ts';
import { useLoad, useNow } from '../hooks.ts';
import { pct } from '../console/ai.ts';
import { SupervisorNav } from './SupervisorNav.tsx';
import { SloCard } from './SloCard.tsx';
import { OperatorOptions, PresenceTag } from '../live/people.tsx';
import { href, navigate } from '../router.ts';
import { useSession } from '../session.tsx';
import { relAge } from '../time.ts';

const POLL_MS = 2000;

function useWallboard(): { data: Wallboard | undefined; error: unknown; refresh: () => void } {
  const [data, setData] = useState<Wallboard | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const kick = useRef<() => void>(() => undefined);
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let ctl: AbortController | null = null;
    const tick = async () => {
      if (timer) clearTimeout(timer);
      ctl?.abort();
      const c = new AbortController();
      ctl = c;
      try {
        const w = await api.wallboard(c.signal);
        if (stopped || c.signal.aborted) return;
        setData(w);
        setError(null);
      } catch (e) {
        if (stopped || c.signal.aborted) return;
        setError(e);
      }
      if (!stopped) timer = setTimeout(() => void tick(), POLL_MS);
    };
    kick.current = () => void tick();
    void tick();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      ctl?.abort();
    };
  }, []);
  const refresh = useCallback(() => kick.current(), []);
  return { data, error, refresh };
}

function secs(s: number | null | undefined): string {
  return s === null || s === undefined ? '—' : dur(s * 1000);
}

function OfferCountdown({ expiresAt, label }: { expiresAt: string; label?: string }) {
  const now = useNow(500);
  const left = Math.max(0, (ms(expiresAt) ?? now) - now);
  return (
    <span className={`mono${left < 5000 ? ' late' : ''}`}>
      {label ? `${label} · ` : ''}
      {left > 0 ? `0:${String(Math.ceil(left / 1000)).padStart(2, '0')}` : 'expiring'}
    </span>
  );
}

function Countdown({ to }: { to: string | null | undefined }) {
  const now = useNow(1000);
  const t = ms(to);
  if (t === null) return <span className="dim">—</span>;
  return <span className={`mono${t - now < 60_000 ? ' late' : ''}`}>{t > now ? dur(t - now) : 'due'}</span>;
}

/* ---------- reassign drawer ---------- */

function ReassignDrawer({
  events,
  operators,
  fixedEventId,
  fromName,
  onClose,
  onDone,
}: {
  /** Events to choose from (one operator's, or the one row). */
  events: EventSummary[];
  operators: WallboardOperator[];
  fixedEventId: string | null;
  fromName: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [eventId, setEventId] = useState<string | null>(fixedEventId ?? events[0]?.id ?? null);
  const [to, setTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const ev = events.find((e) => e.id === eventId) ?? null;
  const candidates = operators.filter((o) => o.userId !== ev?.owner?.id);
  const submit = async () => {
    if (!ev || !to) return;
    setBusy(true);
    setErr(null);
    try {
      let version = ev.version;
      try {
        await api.reassign(ev.id, to, version);
      } catch (e) {
        if (!isConflict(e)) throw e;
        version = (await api.event(ev.id)).version;
        await api.reassign(ev.id, to, version);
      }
      onDone();
      onClose();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer
      icon="users"
      title="Reassign event"
      sub={fromName ? `From ${fromName}. It becomes the new owner's active event, even over their cap.` : 'It becomes the new owner\'s active event, even over their cap.'}
      onClose={onClose}
      onSubmit={() => void submit()}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn pri" disabled={!ev || !to || busy}>
            <Icon name="users" />
            {busy ? 'Reassigning…' : 'Reassign'}
          </button>
        </>
      }
    >
      <div className="sec">
        <div className="lbl">Event</div>
        {events.length === 0 ? (
          <div className="note">{fromName ? `${fromName} has no open events.` : 'No event.'}</div>
        ) : (
          <div role="radiogroup" aria-label="Event" className="optlist">
            {events.map((e) => (
              <button
                key={e.id}
                type="button"
                role="radio"
                aria-checked={eventId === e.id}
                className={`opt${eventId === e.id ? ' on' : ''}`}
                onClick={() => setEventId(e.id)}
                disabled={!!fixedEventId && fixedEventId !== e.id}
              >
                <span className="rd" aria-hidden="true" />
                <span>
                  {e.siteName}
                  <small>
                    {e.displayNo} · {stateLabel(e.state)} · {human(e.primaryAnalytic)}
                  </small>
                </span>
                <span className={`sev s-${SEV_CLS[e.severity]}`}>{SEV_LABEL[e.severity]}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="sec">
        <div className="lbl">To operator</div>
        <OperatorOptions list={candidates} selected={to} onSelect={setTo} overCapOk />
      </div>
      {err ? <InlineError error={err} /> : null}
    </Drawer>
  );
}

/* ---------- screen ---------- */

type Pending = { kind: 'raise'; e: EventSummary } | { kind: 'pause'; op: WallboardOperator } | null;

/** The AI parts of the wallboard: report numbers refresh every minute, the QA queue every 30 s. */
function useAiBoard(on: boolean) {
  const day = useLoad((sig) => (on ? api.agentReport({ days: 1 }, sig) : Promise.resolve(null)), [on]);
  const week = useLoad((sig) => (on ? api.agentReport({ days: 7 }, sig) : Promise.resolve(null)), [on]);
  const qa = useLoad((sig) => (on ? api.qa('pending', sig) : Promise.resolve(null)), [on]);
  const { reload: r1 } = day;
  const { reload: r7 } = week;
  const { reload: rq } = qa;
  useEffect(() => {
    if (!on) return;
    const a = setInterval(() => (r1(), r7()), 60_000);
    const b = setInterval(rq, 30_000);
    return () => (clearInterval(a), clearInterval(b));
  }, [on, r1, r7, rq]);
  return { day: day.data ?? null, week: week.data ?? null, qa: qa.data ?? null, qaError: qa.error };
}

export function WallboardScreen() {
  const s = useSession();
  const { data: w, error, refresh } = useWallboard();
  const [filterOp, setFilterOp] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [reason, setReason] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actErr, setActErr] = useState<unknown>(null);
  const [reassign, setReassign] = useState<{ events: EventSummary[]; fixed: string | null; from: string | null } | null>(null);
  const meId = s.me.userId;
  const agentOn = s.config.features.agent;
  const ai = useAiBoard(agentOn);

  if (error && !w) {
    return (
      <div className="page">
        <ErrorBox error={error} onRetry={refresh} title="Could not load the wallboard" />
      </div>
    );
  }
  if (!w) {
    return (
      <div className="page">
        <Loading label="Loading wallboard…" />
      </div>
    );
  }

  const q = w.queue;
  const ops = w.operators.slice().sort((a, b) => a.displayName.localeCompare(b.displayName));
  const opName = (id: string | null) => ops.find((o) => o.userId === id)?.displayName ?? null;
  const evts = w.events
    .filter((e) => !filterOp || e.owner?.id === filterOp || e.target?.userId === filterOp || e.offer?.userId === filterOp)
    .slice()
    .sort((a, b) => SEV_RANK[a.severity] - SEV_RANK[b.severity] || (ms(a.firstAlarmAt) ?? 0) - (ms(b.firstAlarmAt) ?? 0));
  const online = ops.filter((o) => o.online && o.status !== 'offline').length;
  // From the counts: acceptRate arrives as a percentage (66.7) on the devstack, which the doc does not pin down.
  const acceptPct = w.today.offers ? Math.round((w.today.acceptedWithinSla / w.today.offers) * 1000) / 10 : null;

  const act = async (id: string, label: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    setActErr(null);
    try {
      await fn();
      refresh();
      return true;
    } catch (e) {
      setActErr(new Error(`${label}: ${errorText(e)}`));
      refresh();
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const take = (e: EventSummary) =>
    void act(e.id, `Take ${e.displayNo}`, async () => {
      const d = await api.take(e.id, e.version);
      navigate(href.console(d.id));
    });

  const confirmPending = async () => {
    if (!pending) return;
    if (pending.kind === 'raise') {
      const e = pending.e;
      await act(e.id, `Force raise ${e.displayNo}`, () => api.raise(e.id, reason.trim() || 'Supervisor raise'));
    } else {
      const op = pending.op;
      await act(op.userId, `Pause routing for ${op.displayName}`, () => api.setRouting(op.userId, true, reason.trim() || undefined));
    }
    setPending(null);
    setReason('');
  };

  return (
    <div className="page wallboard">
      <div className="page-h">
        <div>
          <h2>Supervisor wallboard</h2>
          <p>
            {ops.length} operators · {online} online · updated {relAge(w.at)}
            {error ? <span className="warn-t"> · refresh failing, showing the last data</span> : null}
          </p>
        </div>
        <span className="sp" />
        <SupervisorNav current="wallboard" pendingQa={ai.qa?.length ?? null} />
      </div>

      {agentOn && <GlobalPauseBanner on={!!w.ai?.globalPaused} />}
      {agentOn && w.ai && <AiFunnel ai={w.ai} />}

      <div className="tiles">
        <div className="tilek">
          <span className="lbl">Queue now</span>
          <b>{q.total}</b>
          <small>
            {SEVERITIES.filter((sv) => sv !== 'info' || q.bySeverity.info).map((sv, i) => (
              <span key={sv} className={`s-${SEV_CLS[sv]}`} style={{ color: 'var(--sev)' }}>
                {i ? ' · ' : ''}
                {q.bySeverity[sv] ?? 0} {SEV_CLS[sv]}
              </span>
            ))}
          </small>
        </div>
        <div className="tilek">
          <span className="lbl">Oldest waiting</span>
          <Since tag="b" from={q.oldestQueuedAt} />
          <small>
            {q.offered} offered now · {q.overflowed} overflowed
          </small>
        </div>
        <div className={`tilek${q.slaBreaches ? ' badk' : ''}`}>
          <span className="lbl">SLA breaches</span>
          <b>{q.slaBreaches}</b>
          <small>waiting past their SLA</small>
        </div>
        <div className="tilek">
          <span className="lbl">Accept within SLA</span>
          <b>{acceptPct === null ? '—' : `${acceptPct}%`}</b>
          <small>
            Today, {w.today.offers} offers · avg accept {secs(w.today.avgAcceptSeconds)}
          </small>
        </div>
        <div className="tilek">
          <span className="lbl">Active · parked</span>
          <b>
            {w.active} · {w.parked}
          </b>
          <small>
            {w.today.closed} closed today · avg handle {secs(w.today.avgHandleSeconds)}
          </small>
        </div>
        <div className={`tilek${w.heldSyncChanges ? ' warnk' : ''}`}>
          <span className="lbl">Held sync changes</span>
          <b>{w.heldSyncChanges}</b>
          <small>
            <a href={href.imports()}>ERP holds to review</a>
          </small>
        </div>
        <div className={`tilek${w.openWorkItems ? ' warnk' : ''}`}>
          <span className="lbl">Open work items</span>
          <b>{w.openWorkItems}</b>
          <small>expired offers, raises, data issues, QA disagreements</small>
        </div>
        {agentOn && <AiTiles w={w} ai={ai} />}
      </div>

      {pending && (
        <div className="confirmbar" role="group" aria-label="Confirm">
          <span>
            {pending.kind === 'raise' ? (
              <>
                Force raise <b>{pending.e.displayNo}</b> ({pending.e.siteName}) back to {pending.e.owner?.name ?? 'its owner'} now?
              </>
            ) : (
              <>
                Pause routing for <b>{pending.op.displayName}</b>? They get no new offers and keep their events.
              </>
            )}
          </span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" aria-label="Reason" autoFocus onKeyDown={(e) => e.key === 'Enter' && void confirmPending()} />
          <button type="button" className="btn pri" onClick={() => void confirmPending()} disabled={!!busyId}>
            {pending.kind === 'raise' ? 'Raise' : 'Pause routing'}
          </button>
          <button type="button" className="btn" onClick={() => (setPending(null), setReason(''))}>
            Cancel
          </button>
        </div>
      )}
      {actErr ? <InlineError error={actErr} /> : null}

      <div className="cols wb-cols">
        <div className="card2">
          <h3>
            <Icon name="users" />
            Operators
          </h3>
          {ops.length === 0 ? (
            <Empty>No operators yet. People appear after their first sign-in.</Empty>
          ) : (
            <div className="tw">
              <table className="wb-roster">
                <thead>
                  <tr>
                    <th scope="col">Operator</th>
                    <th scope="col">Status</th>
                    <th scope="col">Active</th>
                    <th scope="col">Parked</th>
                    <th scope="col">Offer</th>
                    <th scope="col" title="Accepted · expired · declined today">
                      Today
                    </th>
                    <th scope="col">Avg handle</th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {ops.map((o) => {
                    const offerEv = o.offer ? w.events.find((e) => e.id === o.offer?.eventId) : undefined;
                    const theirs = w.events.filter((e) => e.owner?.id === o.userId);
                    return (
                      <tr key={o.userId} className={filterOp === o.userId ? 'sel' : ''}>
                        <td>
                          <b>{o.displayName}</b>
                          {o.userId === meId && <span className="dim"> (you)</span>}
                          <div className="note">{[o.groups.map((g) => (g.primary ? `${g.name}*` : g.name)).join(', '), o.skills.join(', ')].filter(Boolean).join(' · ') || '—'}</div>
                        </td>
                        <td className="nowrap">
                          <PresenceTag p={o} />
                          {o.routingPaused && <span className="pill warn tiny-pill">paused</span>}
                          <div className="note mono">{relAge(o.since)}</div>
                        </td>
                        <td className={`mono${o.activeCount > o.cap ? ' late' : ''}`}>
                          {o.activeCount}/{o.cap}
                        </td>
                        <td className="mono">{o.parkedCount}</td>
                        <td className="nowrap">{o.offer ? <OfferCountdown expiresAt={o.offer.expiresAt} label={offerEv?.displayNo} /> : <span className="dim">—</span>}</td>
                        <td className="mono nowrap" title="accepted · expired · declined">
                          <span className="ok-t">{o.acceptedToday}</span> · <span className={o.expiredToday ? 'warn-t' : ''}>{o.expiredToday}</span> · {o.declinedToday}
                        </td>
                        <td className="mono">{secs(o.avgHandleSeconds)}</td>
                        <td className="nowrap">
                          <span className="btns">
                            {o.routingPaused ? (
                              <button
                                type="button"
                                className="btn sm"
                                disabled={busyId === o.userId}
                                onClick={() =>
                                  void act(o.userId, `Resume routing for ${o.displayName}`, () => api.setRouting(o.userId, false))
                                }
                              >
                                <Icon name="play" />
                                Resume
                              </button>
                            ) : (
                              <button type="button" className="btn sm" disabled={busyId === o.userId} onClick={() => (setPending({ kind: 'pause', op: o }), setReason(''))} title="No new offers; they keep their events">
                                <Icon name="pause" />
                                Pause
                              </button>
                            )}
                            <button type="button" className="btn sm" disabled={!theirs.length} onClick={() => setReassign({ events: theirs, fixed: null, from: o.displayName })} title="Move one of their events to someone else">
                              <Icon name="users" />
                              Reassign
                            </button>
                            <button type="button" className={`btn sm${filterOp === o.userId ? ' on' : ''}`} aria-pressed={filterOp === o.userId} onClick={() => setFilterOp((f) => (f === o.userId ? null : o.userId))}>
                              <Icon name="eye" />
                              Events
                            </button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card2">
          <h3>
            <Icon name="alert" />
            Alarm storms
          </h3>
          {w.storms.length === 0 ? (
            <div className="note">No storms right now.</div>
          ) : (
            w.storms.map((st) => (
              <div key={st.eventId} className="trow">
                <div>
                  <b>{st.siteName}</b>
                  <div className="note">
                    <a href={href.console(st.eventId)}>{st.displayNo}</a> · {st.cameraIds.length} camera{st.cameraIds.length === 1 ? '' : 's'}
                  </div>
                </div>
                <span className="sp" />
                <span className="pill warn">storm</span>
              </div>
            ))
          )}
          {agentOn && <QaCard items={ai.qa} error={ai.qaError} />}
        </div>
      </div>

      <SloCard />

      <div className="card2 flush">
        <div className="card-h pad">
          <h3>
            <Icon name="list" />
            Open events · {evts.length}
          </h3>
          {filterOp && (
            <span className="card-r">
              <span className="pill info">
                {opName(filterOp) ?? 'Operator'}'s events
                <button type="button" className="chipx" onClick={() => setFilterOp(null)} aria-label="Show all events">
                  <Icon name="x" />
                </button>
              </span>
            </span>
          )}
        </div>
        {evts.length === 0 ? (
          <Empty>{filterOp ? 'No open events for this operator.' : 'No open events.'}</Empty>
        ) : (
          <div className="tw">
            <table className="evt">
              <thead>
                <tr>
                  <th scope="col">Sev</th>
                  <th scope="col">Event</th>
                  <th scope="col">Site</th>
                  <th scope="col">State</th>
                  <th scope="col">Owner / target</th>
                  <th scope="col">Park</th>
                  <th scope="col">Age</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {evts.map((e) => (
                  <EventRow
                    key={e.id}
                    e={e}
                    meId={meId}
                    busy={busyId === e.id}
                    onTake={() => take(e)}
                    onRaise={() => (setPending({ kind: 'raise', e }), setReason(''))}
                    onReassign={() => setReassign({ events: [e], fixed: e.id, from: e.owner?.name ?? null })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {reassign && (
        <ReassignDrawer events={reassign.events} fixedEventId={reassign.fixed} fromName={reassign.from} operators={ops} onClose={() => setReassign(null)} onDone={refresh} />
      )}
    </div>
  );
}

function EventRow({ e, meId, busy, onTake, onRaise, onReassign }: { e: EventSummary; meId: string; busy: boolean; onTake: () => void; onRaise: () => void; onReassign: () => void }) {
  const now = useNow(1000);
  const waiting = e.state === 'queued' || e.state === 'transferred' || e.state === 'offered';
  const fromAi = e.state === 'agent_working' || e.state === 'settling';
  const canTake = waiting || fromAi || (e.state === 'active' && e.owner?.id !== meId);
  const wait = waitingFor(e, now, meId);
  return (
    <tr className={`s-${SEV_CLS[e.severity]}${e.raised ? ' held' : ''}`}>
      <td className="sevcell">
        <span className="sev">{SEV_LABEL[e.severity]}</span>
      </td>
      <td className="nowrap">
        <a href={href.console(e.id)} className="mono">
          {e.displayNo}
        </a>
        {e.overflowed && (
          <span className="pill info tiny-pill" title="Moved to the overflow group">
            overflow
          </span>
        )}
        {e.raised && <span className="pill warn tiny-pill">raised</span>}
        {e.commissioning && <span className="pill warn tiny-pill">commissioning</span>}
      </td>
      <td>
        <span className="strong">{e.siteName}</span>
        <div className="note">
          {human(e.primaryAnalytic)} · {camShort(e.primaryCameraName)}
        </div>
      </td>
      <td className="nowrap">
        <span title={stateHint(e.state) ?? undefined} className={e.state === 'agent_working' || e.state === 'settling' ? 'ai-t' : undefined}>
          {stateLabel(e.state)}
        </span>
        {e.ai?.verdict && (
          <div>
            <VerdictChip ai={e.ai} size="sm" />
          </div>
        )}
        {e.agentPaused && <span className="pill tiny-pill">agent paused</span>}
      </td>
      <td>
        {e.owner ? e.owner.name : wait ? <span className="muted">{wait}</span> : <span className="dim">—</span>}
        {e.transferNote && <div className="note">“{e.transferNote}”</div>}
      </td>
      <td className="nowrap">
        {e.park ? (
          <>
            {parkReasonLabel(e.park.reason)}
            <div className="note">
              raises in <Countdown to={e.park.fallbackAt} />
            </div>
          </>
        ) : (
          <span className="dim">—</span>
        )}
      </td>
      <td className="mono nowrap">
        <Since from={e.firstAlarmAt} lateAt={waiting ? e.slaDueAt : null} className="age" />
      </td>
      <td className="nowrap">
        <span className="btns">
          {canTake && (
            <button type="button" className="btn sm" disabled={busy} onClick={onTake} title={fromAi ? (e.state === 'settling' ? 'Override the auto-resolve: the event becomes yours' : 'Take it from the AI agent') : undefined}>
              <Icon name="users" />
              {fromAi ? 'Take from AI' : 'Take'}
            </button>
          )}
          {e.state === 'parked' && (
            <button type="button" className="btn sm" disabled={busy} onClick={onRaise} title="Raise it back to its owner now">
              <Icon name="park" />
              Force raise
            </button>
          )}
          {e.state !== 'agent_working' && e.state !== 'settling' && (
            <button type="button" className="btn sm" disabled={busy} onClick={onReassign} title="Make it an operator's active event">
              Reassign
            </button>
          )}
          <a className="btn sm" href={href.console(e.id)} title="Open in the console (read-only unless it is yours)">
            <Icon name="eye" />
          </a>
        </span>
      </td>
    </tr>
  );
}

/* ---------- Phase 3: AI tiles and the QA queue ---------- */

/**
 * Tonight's funnel (prototype renderNav #funnel and the queue's "AI funnel tonight" bar), from the wallboard's `ai`
 * block: alarms in → AI resolved → to operators. "Today" is since midnight server time.
 */
function AiFunnel({ ai }: { ai: WallboardAi }) {
  const t = ai.today;
  const base = Math.max(1, t.events);
  return (
    <div className="aifunnel card2" aria-label="AI funnel today">
      <div className="funnel">
        <span className="lbl">Today</span>
        <div className="f">
          <b>{t.alarms.toLocaleString()}</b>
          <span>Alarms in</span>
        </div>
        <div className="f">
          <b>{t.events.toLocaleString()}</b>
          <span>Events</span>
        </div>
        <div className="f ai">
          <b>{t.aiResolved.toLocaleString()}</b>
          <span>AI resolved · {t.events ? pct(t.aiResolved / t.events, 1) : '0%'}</span>
        </div>
        <div className="f">
          <b>{t.toOperators.toLocaleString()}</b>
          <span>To operators · {t.events ? pct(t.toOperators / t.events, 1) : '0%'}</span>
        </div>
      </div>
      <div className="bar" aria-hidden="true">
        <i style={{ width: `${(t.aiResolved / base) * 100}%`, background: 'var(--ai)' }} />
        <i style={{ width: `${(t.toOperators / base) * 100}%`, background: 'var(--accent)' }} />
      </div>
      <div className="legend">
        <span className="ai-t">AI resolved {t.events ? pct(t.aiResolved / t.events) : '0%'} of events</span>
        <span style={{ color: 'var(--accent)' }}>Escalated {t.events ? pct(t.toOperators / t.events) : '0%'}</span>
      </div>
    </div>
  );
}

/** "The AI agent is paused for all sites" (who, why) above the wallboard. */
function GlobalPauseBanner({ on }: { on: boolean }) {
  const pol = useLoad((sig) => (on ? api.aiSetup(sig).then((s) => s.global) : Promise.resolve(null)), [on]);
  if (!on) return null;
  const g = pol.data;
  return (
    <div className="pausebar" role="status">
      <Icon name="pause" />
      <span>
        <b>The AI agent is paused for all sites.</b> Every event goes to an operator.
        {g?.pausedByName ? ` Paused by ${g.pausedByName}.` : ''}
        {g?.pauseReason ? ` Reason: “${g.pauseReason}”.` : ''}
      </span>
      <span className="sp" />
      <a className="btn" href={href.setup('ai')}>
        Setup → AI
      </a>
    </div>
  );
}

function AiTiles({ w, ai }: { w: Wallboard; ai: ReturnType<typeof useAiBoard> }) {
  const now = w.ai?.now;
  const working = now?.triaging ?? w.events.filter((e) => e.state === 'agent_working').length;
  const settling = now?.autoResolving ?? w.events.filter((e) => e.state === 'settling').length;
  const week = ai.week;
  const qaRev = week ? week.sites.reduce((a, x) => a + x.qa.reviewed, 0) : 0;
  const qaDis = week ? week.sites.reduce((a, x) => a + x.qa.disagree, 0) : 0;
  const missed = week?.totals.wouldResolveMissed ?? null;
  const qaPending = w.ai?.qaPending ?? ai.qa?.length ?? null;
  return (
    <>
      <div className="tilek aik">
        <span className="lbl">AI triaging now</span>
        <b>{working}</b>
        <small>events the agent holds, before a human</small>
      </div>
      <div className="tilek aik">
        <span className="lbl">Auto-resolving</span>
        <b>{settling}</b>
        <small>closing unless another alarm arrives</small>
      </div>
      {now && (
        <div className={`tilek ${now.pendingApprovals ? 'askk' : 'aik'}`}>
          <span className="lbl">Agent requests waiting</span>
          <b>{now.pendingApprovals}</b>
          <small>approvals operators have to answer (20 s each)</small>
        </div>
      )}
      {now && (
        <div className="tilek aik">
          <span className="lbl">AI watches active</span>
          <b>{now.activeWatches}</b>
          <small>parked events waiting for police, a subject, a vehicle</small>
        </div>
      )}
      <div className={`tilek ${qaPending ? 'warnk' : 'aik'}`}>
        <span className="lbl">QA pending</span>
        <b>{qaPending ?? '—'}</b>
        <small>
          <a href={href.qa()}>sampled auto-resolves to review blind</a>
        </small>
      </div>
      <div className={`tilek ${missed ? 'missk' : 'aik'}`}>
        <span className="lbl">AI missed threats · 7 days</span>
        <b>{missed ?? '—'}</b>
        <small>
          {week ? `would have closed a real event · QA: ${qaDis} of ${qaRev} reviews disagree` : 'loading'} · <a href={href.aiReport(7)}>AI report</a>
        </small>
      </div>
    </>
  );
}

function QaCard({ items, error }: { items: QaItem[] | null; error: unknown }) {
  const list = items ?? [];
  return (
    <>
      <h3 className="mt">
        <Icon name="spark" className="ai-t" />
        AI QA review <span className="dim small">· sampled auto-resolves, reviewed blind</span>
      </h3>
      {error && !items ? (
        <InlineError error={error} />
      ) : !items ? (
        <div className="note">Loading…</div>
      ) : list.length === 0 ? (
        <div className="note">No samples waiting.</div>
      ) : (
        <>
          {list.slice(0, 4).map((it) => (
            <div key={it.eventId} className="trow">
              <div>
                <b>{it.event.siteName}</b>
                <div className="note">
                  <span className="mono">{it.event.displayNo}</span> · sampled {relAge(it.sampledAt)}
                </div>
              </div>
              <span className="sp" />
              <a className="btn sm" href={href.qa('pending', it.eventId)}>
                Review
              </a>
            </div>
          ))}
          {list.length > 4 && (
            <a href={href.qa()} className="note">
              {list.length - 4} more waiting
            </a>
          )}
        </>
      )}
    </>
  );
}
